import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";

/**
 * @typedef {object} AnnouncementItem
 * @property {number} id - Notification ID
 * @property {number} batch_id - Batch ID for the announcement group
 * @property {string} title - Announcement title
 * @property {string} description - Announcement description
 * @property {{url: string}[]|null} media_url - Attached media urls
 * @property {string} created_at - Created timestamp
 * @property {number} total_recipients - Total number of recipients
 * @property {string} total_read - Total read count
 * @property {string} total_unread - Total unread count
 * @property {string} total_dismissed - Total dismissed count
 * @property {string} total_users - Total user recipients
 * @property {string} total_students - Total student recipients
 * @property {string} receiver_names - Comma-separated receiver names
 * @property {string[]} receiver_roles - Array of receiver roles
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetAllAnnouncementsParams
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 * @property {number} [batch_id] - Filter by batch ID
 * @property {string} [title] - Filter by title
 * @property {"unread"|"read"|"all"} [read_status] - Filter by read status
 */

/**
 * Custom hook to fetch all announcements with pagination
 */
export const useGetAllAnnouncements = () => {
  const [announcements, setAnnouncements] = useState(
    /** @type {AnnouncementItem[] | null | undefined} */ (undefined),
  );
  const [meta, setMeta] = useState(
    /** @type {PaginationMeta | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Fetch all announcements with pagination
   * @async
   * @param {GetAllAnnouncementsParams} params
   * @returns {Promise<void>}
   */
  const getAllAnnouncements = async ({
    page = 1,
    per_page = ITEMS_PER_PAGE,
    batch_id,
    title,
    read_status = "all",
    type,
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("per_page", per_page);

      if (batch_id) {
        query.append("filter[batch_id]", batch_id);
      }
      if (title) {
        query.append("filter[title]", title);
      }
      if (read_status) {
        query.append("filter[read_status]", read_status);
      }

      const domain = type === "pcnormal" ? apiUrl : TENANT_DOMAIN;
      const url = `${domain}/notifications?${query.toString()}`;

      let response;

      if (type === "pcnormal") {
        const raw = await fetchWithAuth(url, { method: "GET" });
        const parsed = await raw.json(); // ✅ parse the Response
        setAnnouncements(parsed?.data || []);
        setMeta(parsed?.meta || null);
        return;
      }

      // only runs for pnormal
      response = await axiosInstance.get(url);
      setAnnouncements(response.data?.data || []);
      setMeta(response.data?.meta || null);
    } catch (err) {
      setAnnouncements(null);
      setMeta(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    announcements,
    meta,
    loading,
    error,
    getAllAnnouncements,
  };
};
